import { type ComputedRef, computed } from 'vue'
import type { ChatMessage } from '../types/ChatMessage'
import { parseQuoteFromContent } from '../utils/messageFormatter'
import { extractImageUrlsFromText, getFileName, isImageFile, isMySender, isVideoFile } from '../utils'
import { toApiDate } from '@/utils/formatters'
import { formatFilesMediaGroupDate } from '@/utils/russianDateFormatter'

export interface GalleryMediaItem {
  id: string
  fileUrl: string
  mimeType?: string
  kind: 'image' | 'video'
  /** Matrix video poster (`info.thumbnail_url`). */
  posterUrl?: string
  timestamp: number
  senderAvatar?: string
  senderName: string
}

export interface GalleryFileItem {
  id: string
  fileUrl: string
  mimeType?: string
  name: string
  timestamp: number
}

export interface FilesMediaDateGroup {
  dateKey: string
  label: string
  media: GalleryMediaItem[]
  files: GalleryFileItem[]
}

interface GalleryContext {
  profileAvatar?: string
  profileName?: string
  currentUserId: string | number | null
  matrixUserId?: string | null
  contactAvatar?: string
  contactName?: string
  isGroupLike?: boolean
}

function getMessageAuthorName(message: ChatMessage): string {
  const msgAny = message as ChatMessage & {
    author_name?: string
    name?: string
  }

  return msgAny.created_by?.full_name
    || msgAny.author_name
    || msgAny.name
    || (msgAny.created_by?.first_name && msgAny.created_by?.last_name
      ? `${msgAny.created_by.first_name} ${msgAny.created_by.last_name}`.trim()
      : '')
    || msgAny.created_by?.user_name
    || msgAny.created_by?.username
    || 'Пользователь'
}

function getMessageAvatar(message: ChatMessage): string | undefined {
  const msgAny = message as ChatMessage & { avatar?: string }
  return msgAny.avatar || msgAny.created_by?.avatar
}

function resolveSender(
  message: ChatMessage,
  context: GalleryContext,
): { avatar?: string; name: string } {
  const isMine = context.currentUserId
    ? isMySender(message.senderId, context.currentUserId, context.matrixUserId)
    : false

  if (isMine) {
    return {
      avatar: context.profileAvatar,
      name: context.profileName || 'Я',
    }
  }

  return {
    avatar: getMessageAvatar(message) || (context.isGroupLike ? undefined : context.contactAvatar),
    name: getMessageAuthorName(message) || context.contactName || 'Пользователь',
  }
}

function getMessageText(message: ChatMessage): string {
  const msgAny = message as ChatMessage & { content?: string }
  const content = message.message || msgAny.content || ''

  return parseQuoteFromContent(content).remainingText
}

function getInlineImageUrls(message: ChatMessage): string[] {
  if (message.attachments?.length) {
    return []
  }

  return extractImageUrlsFromText(getMessageText(message))
}

function ensureGroup(
  groups: Map<string, FilesMediaDateGroup>,
  dateKey: string,
  messageTime: string,
): FilesMediaDateGroup {
  if (!groups.has(dateKey)) {
    groups.set(dateKey, {
      dateKey,
      label: formatFilesMediaGroupDate(messageTime),
      media: [],
      files: [],
    })
  }

  return groups.get(dateKey)!
}

function collectFromMessages(
  messages: ChatMessage[],
  context: GalleryContext,
): FilesMediaDateGroup[] {
  const groups = new Map<string, FilesMediaDateGroup>()
  const seenUrls = new Set<string>()

  for (const message of messages) {
    if (message.is_deleted) {
      continue
    }

    const hasAttachments = Boolean(message.attachments?.length)
    const inlineImageUrls = getInlineImageUrls(message)

    if (!hasAttachments && inlineImageUrls.length === 0) {
      continue
    }

    const dateKey = toApiDate(message.time) || 'unknown'
    const timestamp = new Date(message.time).getTime() || 0
    const sender = resolveSender(message, context)
    const group = ensureGroup(groups, dateKey, message.time)

    if (hasAttachments) {
      for (const attachment of message.attachments!) {
        const fileUrl = attachment.file
        if (!fileUrl || seenUrls.has(fileUrl)) {
          continue
        }

        seenUrls.add(fileUrl)
        const itemId = `${message.matrix_event_id || message.id || timestamp}-${attachment.id}`

        if (isImageFile(fileUrl, attachment.mime_type)) {
          group.media.push({
            id: itemId,
            fileUrl,
            mimeType: attachment.mime_type,
            kind: 'image',
            timestamp,
            senderAvatar: sender.avatar,
            senderName: sender.name,
          })
          continue
        }

        if (isVideoFile(fileUrl, attachment.mime_type)) {
          group.media.push({
            id: itemId,
            fileUrl,
            mimeType: attachment.mime_type,
            kind: 'video',
            posterUrl: attachment.thumbnail,
            timestamp,
            senderAvatar: sender.avatar,
            senderName: sender.name,
          })
          continue
        }

        group.files.push({
          id: itemId,
          fileUrl,
          mimeType: attachment.mime_type,
          name: attachment.name || getFileName(fileUrl),
          timestamp,
        })
      }
    }

    inlineImageUrls.forEach((fileUrl, index) => {
      if (seenUrls.has(fileUrl)) {
        return
      }

      seenUrls.add(fileUrl)
      group.media.push({
        id: `${message.matrix_event_id || message.id || timestamp}-inline-${index}`,
        fileUrl,
        kind: 'image',
        timestamp,
        senderAvatar: sender.avatar,
        senderName: sender.name,
      })
    })
  }

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      media: [...group.media].sort((a, b) => b.timestamp - a.timestamp),
      files: [...group.files].sort((a, b) => b.timestamp - a.timestamp),
    }))
    .filter(group => group.media.length > 0 || group.files.length > 0)
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey))
}

/** Плоский список медиа чата по времени (старые → новые) для lightbox. */
export function flattenChatMedia(messages: ChatMessage[]): GalleryMediaItem[] {
  const groups = collectFromMessages(messages, {
    currentUserId: null,
    profileName: '',
  })

  return groups
    .flatMap(group => group.media)
    .sort((a, b) => a.timestamp - b.timestamp)
}

export function useFilesAndMediaGallery(
  messages: ComputedRef<ChatMessage[] | undefined>,
  context: ComputedRef<GalleryContext>,
) {
  const dateGroups = computed(() => {
    const items = messages.value
    if (!items?.length) {
      return []
    }

    return collectFromMessages(items, context.value)
  })

  const totalCount = computed(() => dateGroups.value.reduce(
    (sum, group) => sum + group.media.length + group.files.length,
    0,
  ))

  return {
    dateGroups,
    totalCount,
  }
}
